import React, { useEffect, useState, useRef } from 'react';
import { PolylineF } from '@react-google-maps/api';
import { usePlan } from '../../hooks/PlanProvider';
import { PathSymbol } from 'src/types/interfaces';
import { DirectionsRequest, GoogleMapsClient } from 'src/lib/maps_client';

interface PathProps {
  originCoords: google.maps.LatLngLiteral;
  destinationCoords: google.maps.LatLngLiteral;
  isActive?: boolean;
  routePolyline?: string;
  mapsClient: GoogleMapsClient;
}

const Path: React.FC<PathProps> = ({ originCoords, destinationCoords, isActive = false, mapsClient, routePolyline }) => {
  const [pathCoordinates, setPathCoordinates] = useState<google.maps.LatLngLiteral[]>([]);
  const [directionsLoaded, setDirectionsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const animationRef = useRef({
    isRunning: false,
    offset: 0,
    dotOpacity: 1,
    glowIntensity: 0,
    animationFrameId: 0,
    prevTime: 0,
    symbolsCache: null as PathSymbol[] | null
  });

  const [totalPathLength, setTotalPathLength] = useState(0);
  const [segmentLengths, setSegmentLengths] = useState<number[]>([]);
  const { userPlans } = usePlan();

  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);

  useEffect(() => {
    const fetchDirectionsForSegments = async () => {
      try {
        let allCoordinates: google.maps.LatLngLiteral[] = [];
        let lengths: number[] = [];
        let totalLength = 0;

        const request: DirectionsRequest = {
          origin: originCoords,
          destination: destinationCoords,
          travelMode: google.maps.TravelMode.WALKING,
          optimizeWaypoints: true
        };

        try {
          const result = await mapsClient.directions(request);
          if (result.routes[0]?.overview_path) {
            const segmentPath = result.routes[0].overview_path.map((latlng) => latlng.toJSON() as google.maps.LatLngLiteral);
            allCoordinates = allCoordinates.concat(segmentPath);

            let segmentLength = 0;
            for (let j = 0; j < segmentPath.length - 1; j++) {
              segmentLength += google.maps.geometry.spherical.computeDistanceBetween(
                segmentPath[j],
                segmentPath[j + 1]
              );
            }
            lengths.push(segmentLength);
            totalLength += segmentLength;
          }
        } catch (error) {
          console.error(`Error fetching directions to ${destinationCoords} from ${originCoords}}:`, error);
          const straightLength = google.maps.geometry.spherical.computeDistanceBetween(originCoords, destinationCoords);
          lengths.push(straightLength);
          totalLength += straightLength;
        }

        setPathCoordinates(allCoordinates);
        setSegmentLengths(lengths);
        setTotalPathLength(totalLength);
      } catch (error) {
        console.error('Error fetching directions:', error);
      }
    };

    fetchDirectionsForSegments().then(() => setDirectionsLoaded(true));
  }, [userPlans, originCoords, destinationCoords, mapsClient]);

  useEffect(() => {
    if (isActive && directionsLoaded) {
      startAnimation();
    } else {
      stopAnimation();
    }

    return () => {
      stopAnimation();
    };
  }, [isActive, directionsLoaded]);

  const startAnimation = () => {
    if (animationRef.current.isRunning) return;

    animationRef.current.isRunning = true;
    animationRef.current.offset = 0;
    animationRef.current.dotOpacity = 0;
    animationRef.current.glowIntensity = 0;
    animationRef.current.prevTime = performance.now();
    animationRef.current.symbolsCache = null;

    setForceUpdateCounter(c => c + 1);
    animationRef.current.animationFrameId = requestAnimationFrame(animateFrame);
  };

  const stopAnimation = () => {
    if (animationRef.current.animationFrameId) {
      cancelAnimationFrame(animationRef.current.animationFrameId);
    }

    animationRef.current.isRunning = false;
    animationRef.current.offset = 0;
    animationRef.current.dotOpacity = 1;
    animationRef.current.glowIntensity = 0;
    animationRef.current.symbolsCache = null;

    setForceUpdateCounter(c => c + 1);
  };

  const animateFrame = (currentTime: number) => {
    if (!animationRef.current.isRunning) return;

    const prevTime = animationRef.current.prevTime || currentTime;
    const deltaTime = (currentTime - prevTime) / 1000;
    animationRef.current.prevTime = currentTime;

    const increment = (100 / 5) * deltaTime;
    const newOffset = (animationRef.current.offset + increment) % 100;

    let newDotOpacity = 1;
    if (newOffset < 2) {
      newDotOpacity = newOffset / 2;
    } else if (newOffset > 98) {
      newDotOpacity = (100 - newOffset) / 2;
    }

    const velocity = Math.min(100, increment / Math.max(0.001, deltaTime));
    const targetGlow = Math.min(1, velocity * 0.05);
    const newGlowIntensity = animationRef.current.glowIntensity +
      (targetGlow - animationRef.current.glowIntensity) * 0.1;

    animationRef.current.offset = newOffset;
    animationRef.current.dotOpacity = newDotOpacity;
    animationRef.current.glowIntensity = newGlowIntensity;
    animationRef.current.symbolsCache = null;

    setForceUpdateCounter(c => c + 1);
    animationRef.current.animationFrameId = requestAnimationFrame(animateFrame);
  };

  const createPathSymbols = (): PathSymbol[] => {
    if (!isActive) return [];

    if (animationRef.current.symbolsCache) {
      return animationRef.current.symbolsCache;
    }

    const { offset, dotOpacity, glowIntensity } = animationRef.current;
    const baseGlowIntensity = 0.3 + (glowIntensity * 0.4);

    const symbols: PathSymbol[] = [
      {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 4,
          fillColor: '#FFFFF0',
          fillOpacity: 0.95 * dotOpacity,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
        offset: `${offset}%`,
      },
      {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8 + (glowIntensity * 4),
          fillColor: '#00FFFF',
          fillOpacity: baseGlowIntensity * dotOpacity,
          strokeWeight: 0,
        },
        offset: `${offset}%`,
      },
      {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12 + (glowIntensity * 6),
          fillColor: '#00FFFF',
          fillOpacity: baseGlowIntensity * 0.5 * dotOpacity,
          strokeWeight: 0,
        },
        offset: `${offset}%`,
      }
    ];

    const trailLength = 8;
    for (let i = 0; i < trailLength; i++) {
      const fadeOffset = i / trailLength;
      const trailOpacity = (0.3 - (fadeOffset * 0.25)) * (1 + glowIntensity) * dotOpacity;

      symbols.push({
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 3 - (fadeOffset * 2) + (glowIntensity * 2),
          fillColor: '#00FFFF',
          fillOpacity: trailOpacity,
          strokeWeight: 0,
        },
        offset: `${Math.max(0, offset - (i * 4 * (1 + glowIntensity)))}%`,
      });
    }

    animationRef.current.symbolsCache = symbols;
    return symbols;
  };

  const pathOptions: google.maps.PolylineOptions = {
    geodesic: true,
    strokeColor: 'rgb(255, 91, 165)',
    strokeOpacity: isHovered ? 0.9 : isActive ? 0.8 : 0.5,
    strokeWeight: isHovered ? 6 : isActive ? 5 : 3,
    icons: createPathSymbols(),
    zIndex: isActive ? 2 : 1,
  };

  return (
    <PolylineF
      key={`polyline-${destinationCoords.lat}-${forceUpdateCounter}`}
      path={pathCoordinates}
      options={pathOptions}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    />
  );
};

export default Path;
